// frontend/src/hooks/useSucursal.js
// ─────────────────────────────────────────────────────────────────────────────
// Hook de contexto de sucursal para el DISPOSITIVO actual.
//
// ARQUITECTURA CORRECTA:
//   - users = TENANT (dueño del negocio, paga el ERP)
//   - sucursal = Elección a nivel DISPOSITIVO/NAVEGADOR (localStorage)
//   - Múltiples tablets/PCs pueden compartir el mismo user_id con distintas sucursales
//
// Uso:
//   const { sucursalId, sucursalNombre, isOfflineBranch } = useSucursal();
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';

const LS_KEY_ID      = 'sucursal_id';
const LS_KEY_NOMBRE  = 'sucursal_nombre';
const LS_KEY_OFFLINE = 'sucursal_es_offline';
const LS_KEY_TIPO    = 'cuenta_tipo';    // 'admin' | 'cajero' | 'terminal'
const LS_KEY_ROL     = 'empleado_rol';   // 'vendedora' | 'encargada' (solo si cuenta_tipo === 'cajero')
const LS_KEY_MODULES = 'sucursal_modules'; // módulos propios de la sucursal activa (solo Dueño/admin)

// ─────────────────────────────────────────────────────────────────────────────
// Funciones puras de lectura/escritura en localStorage
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Guarda la sucursal seleccionada por el operador en el dispositivo actual.
 */
export function setSucursalActiva(sucursal) {
  localStorage.setItem(LS_KEY_ID,      String(sucursal.id));
  localStorage.setItem(LS_KEY_NOMBRE,  sucursal.nombre || '');
  localStorage.setItem(LS_KEY_OFFLINE, sucursal.es_offline ? '1' : '0');
  if (sucursal.modules) {
    localStorage.setItem(LS_KEY_MODULES, JSON.stringify(sucursal.modules));
  } else {
    localStorage.removeItem(LS_KEY_MODULES);
  }
  window.dispatchEvent(new Event('sucursal-changed'));
}

/**
 * Vuelve al modo "Todas las sucursales" para el Dueño en este dispositivo —
 * sin cerrar sesión, solo deja de restringir por una sucursal en particular.
 * (No se usa para cajero/terminal: ellos siempre tienen una sucursal fija.)
 */
export function clearSucursalActiva() {
  localStorage.removeItem(LS_KEY_ID);
  localStorage.removeItem(LS_KEY_NOMBRE);
  localStorage.removeItem(LS_KEY_OFFLINE);
  localStorage.removeItem(LS_KEY_MODULES);
  window.dispatchEvent(new Event('sucursal-changed'));
}

/**
 * Persiste la sucursal fija de un cajero (se llama al hacer cajero-login).
 * A diferencia de setSucursalActiva, también guarda el tipo 'cajero'.
 */
const LS_KEY_EMPLEADO = 'empleado_nombre';
const LS_KEY_PERMITIDAS = 'empleado_sucursales_permitidas';

export function persistCajeroSession(empleado) {
  localStorage.setItem(LS_KEY_ID,        String(empleado.sucursal_id));
  localStorage.setItem(LS_KEY_NOMBRE,    empleado.sucursal_nombre || '');
  localStorage.setItem(LS_KEY_OFFLINE,   empleado.es_offline ? '1' : '0');
  localStorage.setItem(LS_KEY_EMPLEADO,  empleado.nombre || '');
  if (empleado.modules || empleado.sucursal_modules) {
    localStorage.setItem(LS_KEY_MODULES, JSON.stringify(empleado.modules || empleado.sucursal_modules));
  }
  if (empleado.sucursales_permitidas) {
    localStorage.setItem(LS_KEY_PERMITIDAS, JSON.stringify(empleado.sucursales_permitidas));
  }
  localStorage.setItem(LS_KEY_TIPO,      'cajero');
  localStorage.setItem(LS_KEY_ROL,       empleado.rol || 'vendedora');
  window.dispatchEvent(new Event('sucursal-changed'));
}

/**
 * Persiste la sesión de TERMINAL/KIOSCO (se llama al hacer login con el
 * `codigo_acceso` de una sucursal). Fija el dispositivo a esa sucursal pero
 * todavía no identifica a un cajero — eso ocurre en el siguiente paso (PIN).
 */
export function persistTerminalSession(sucursal) {
  localStorage.setItem(LS_KEY_ID,      String(sucursal.id));
  localStorage.setItem(LS_KEY_NOMBRE,  sucursal.nombre || '');
  localStorage.setItem(LS_KEY_OFFLINE, sucursal.es_offline ? '1' : '0');
  if (sucursal.modules) {
    localStorage.setItem(LS_KEY_MODULES, JSON.stringify(sucursal.modules));
  } else {
    localStorage.removeItem(LS_KEY_MODULES);
  }
  localStorage.setItem(LS_KEY_TIPO,    'terminal');
  window.dispatchEvent(new Event('sucursal-changed'));
}

/** Lee el sucursal_id del dispositivo. */
export function getSucursalId() {
  const id = localStorage.getItem(LS_KEY_ID);
  return id ? parseInt(id, 10) : null;
}

/** Lee el nombre de la sucursal del dispositivo. */
export function getSucursalNombre() {
  return localStorage.getItem(LS_KEY_NOMBRE) || '';
}

/** ¿Este dispositivo ya tiene una sucursal asignada? */
export function hasSucursalAsignada() {
  return Boolean(localStorage.getItem(LS_KEY_ID));
}

/** ¿El tipo de cuenta activa es admin (dueño)? */
export function isAdmin() {
  const tipo = localStorage.getItem(LS_KEY_TIPO);
  return tipo !== 'cajero' && tipo !== 'terminal'; // por defecto admin si no hay nada guardado
}

/** ¿Es un cajero ya identificado con su PIN? */
export function isCajero() {
  return localStorage.getItem(LS_KEY_TIPO) === 'cajero';
}

/** ¿Es una terminal/kiosco activada por codigo_acceso, sin cajero identificado aún? */
export function isTerminal() {
  return localStorage.getItem(LS_KEY_TIPO) === 'terminal';
}

/** Retorna 'admin' | 'cajero' | 'terminal' */
export function getTipoCuenta() {
  return localStorage.getItem(LS_KEY_TIPO) || 'admin';
}

/** Retorna 'vendedora' | 'encargada' | null (null si no es una sesión de cajero) */
export function getRolEmpleado() {
  return isCajero() ? (localStorage.getItem(LS_KEY_ROL) || 'vendedora') : null;
}

/** ¿Es una Encargada de sucursal ya identificada con su PIN? */
export function isEncargada() {
  return isCajero() && getRolEmpleado() === 'encargada';
}

/** Retorna la lista de sucursales permitidas para el cajero activo, o null si no aplica */
export function getSucursalesPermitidas() {
  const raw = localStorage.getItem(LS_KEY_PERMITIDAS);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

/** ¿Es un cajero rotativo con 2 o más sucursales asignadas? */
export function isCajeroRotativo() {
  const permitidas = getSucursalesPermitidas();
  return isCajero() && Array.isArray(permitidas) && permitidas.length > 1;
}

/** Módulos propios de la sucursal activa (null = sin sucursal específica → sin restricción extra). */
export function getSucursalModules() {
  const raw = localStorage.getItem(LS_KEY_MODULES);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

/** Tipo de negocio del tenant ('tienda_general' | 'botica') */
export function getTipoNegocio() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.tipo_negocio || 'tienda_general';
  } catch {
    return 'tienda_general';
  }
}

/** ¿El negocio actual opera en modo Botica / Farmacia? */
export function isBotica() {
  return getTipoNegocio() === 'botica';
}

/** ¿El negocio actual opera en modo Parking / Estacionamiento? */
export function isParking() {
  return getTipoNegocio() === 'parking';
}

/** ¿El negocio actual opera en modo Jugutería / Armables? */
export function isJuguteria() {
  return getTipoNegocio() === 'juguteria';
}

/**
 * Módulos EFECTIVOS = intersección entre lo que el Dueño tiene habilitado en
 * su cuenta y lo que la sucursal activa (si hay una) permite. Se recalcula
 * siempre a partir de ambas fuentes — nunca se confía en un valor guardado
 * aislado de la sucursal, para que si el SuperAdmin le quita un módulo al
 * Dueño después, la sucursal no pueda seguir usándolo solo porque quedó
 * en `true` en su JSONB.
 *
 *   efectivo[x] = userModules[x] && sucursalModules[x] !== false
 *
 * Sin sucursal activa (Dueño en modo "Todas"), devuelve userModules tal cual.
 */
export function getModulosEfectivos(userModules) {
  const sucModules = getSucursalModules();
  if (!sucModules) return userModules || {};
  const efectivos = {};
  for (const key of Object.keys(userModules || {})) {
    efectivos[key] = !!userModules[key] && sucModules[key] !== false;
  }
  return efectivos;
}

/** Limpia la sucursal del localStorage al hacer logout. */
export function clearSucursalData() {
  localStorage.removeItem(LS_KEY_ID);
  localStorage.removeItem(LS_KEY_NOMBRE);
  localStorage.removeItem(LS_KEY_OFFLINE);
  localStorage.removeItem(LS_KEY_TIPO);
  localStorage.removeItem(LS_KEY_ROL);
  localStorage.removeItem(LS_KEY_EMPLEADO);
  localStorage.removeItem(LS_KEY_MODULES);
  localStorage.removeItem(LS_KEY_PERMITIDAS);
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook React
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook que expone los datos de la sucursal activa del dispositivo.
 * Reacciona a cambios de localStorage (cambio de sucursal desde el selector).
 */
export function useSucursal() {
  const read = () => ({
    sucursalId:      getSucursalId(),
    sucursalNombre:  localStorage.getItem(LS_KEY_NOMBRE)  || 'Sin sucursal',
    isOfflineBranch: localStorage.getItem(LS_KEY_OFFLINE) === '1',
  });

  const [state, setState] = useState(read);

  useEffect(() => {
    const refresh = () => setState(read());
    // Escucha el evento custom que emite setSucursalActiva()
    window.addEventListener('sucursal-changed', refresh);
    // Fallback: cambios desde otra pestaña
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('sucursal-changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  return state;
}

export default useSucursal;
